import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Check, Info, SlidersHorizontal } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import RatingFilterRow from '@/components/feature/RatingFilterRow'
import AchievementDetailDialog from '@/components/feature/AchievementDetailDialog'
import TrophyDetailDialog from './TrophyDetailDialog'
import { getAchievements } from '@/api/achievements'
import { generateOriginalTrophy, getOriginalTrophies, getTrophyQuota } from '@/api/trophies'
import type { GenerateMaterial, OriginalTrophy } from '@/api/trophies'
import type { Achievement } from '@/types'
import {
  SELF_RATING_OPTIONS, AI_RATING_OPTIONS, SORT_OPTIONS, DEFAULT_ORDERING, datePresets,
} from '@/lib/achievementFilters'

type Kind = 'achievement' | 'trophy'
type Sel = { kind: Kind; id: number }
type Filters = {
  search?: string
  rating: number[]
  ai_rating: number[]
  date_from?: string
  date_to?: string
  ordering: string
}

export default function TrophyGenerateDialog({
  open,
  onClose,
  onGenerated,
}: {
  open: boolean
  onClose: () => void
  onGenerated?: (trophy: OriginalTrophy) => void
}) {
  const qc = useQueryClient()
  const [selected, setSelected] = useState<Sel[]>([])
  const [supplement, setSupplement] = useState('')
  const [tab, setTab] = useState<Kind>('achievement')
  // 材料行の「詳細」ボタンで開く閲覧用ダイアログ（削除・詳細遷移ボタンは出さない）。
  const [detailAch, setDetailAch] = useState<Achievement | null>(null)
  const [detailTrophy, setDetailTrophy] = useState<OriginalTrophy | null>(null)

  const [searchInput, setSearchInput] = useState('')
  const [filters, setFilters] = useState<Filters>({ rating: [], ai_rating: [], ordering: DEFAULT_ORDERING })
  const [filterOpen, setFilterOpen] = useState(false)

  const { data: quota } = useQuery({ queryKey: ['trophyQuota'], queryFn: getTrophyQuota, enabled: open })
  const { data: achievements = [] } = useQuery({
    queryKey: ['achievements', filters],
    queryFn: () => getAchievements(filters),
    enabled: open,
  })
  const { data: trophies = [] } = useQuery({
    queryKey: ['originalTrophies'],
    queryFn: getOriginalTrophies,
    enabled: open,
  })

  const maxMaterials = quota?.max_materials ?? 20
  const presets = useMemo(() => datePresets(), [])
  const activeFilterCount =
    filters.rating.length + filters.ai_rating.length + (filters.date_from || filters.date_to ? 1 : 0)

  const isSel = (kind: Kind, id: number) => selected.some((s) => s.kind === kind && s.id === id)
  const toggle = (kind: Kind, id: number) => {
    if (isSel(kind, id)) {
      setSelected((prev) => prev.filter((s) => !(s.kind === kind && s.id === id)))
      return
    }
    if (selected.length >= maxMaterials) {
      toast.error(`材料は最大${maxMaterials}個までです`)
      return
    }
    setSelected((prev) => [...prev, { kind, id }])
  }

  const toggleRating = (key: 'rating' | 'ai_rating', value: number) =>
    setFilters((f) => ({
      ...f,
      [key]: f[key].includes(value) ? f[key].filter((v) => v !== value) : [...f[key], value],
    }))

  const applyPreset = (from: string, to: string) =>
    setFilters((f) => ({ ...f, date_from: from || undefined, date_to: to || undefined }))

  const reset = () => {
    setSelected([])
    setSupplement('')
    setTab('achievement')
    setSearchInput('')
    setFilters({ rating: [], ai_rating: [], ordering: DEFAULT_ORDERING })
    setFilterOpen(false)
  }

  const mutation = useMutation({
    mutationFn: () => generateOriginalTrophy(selected as GenerateMaterial[], supplement),
    onSuccess: (trophy) => {
      qc.invalidateQueries({ queryKey: ['originalTrophies'] })
      qc.invalidateQueries({ queryKey: ['trophyQuota'] })
      reset()
      onClose()
      onGenerated?.(trophy)
    },
    onError: (err) => {
      const res = (err as { response?: { status?: number; data?: { detail?: string } } })?.response
      if (res?.status === 429) toast.error(res.data?.detail ?? '生成上限に達しました')
      else if (res?.status === 400) toast.error(res.data?.detail ?? '材料を確認してください')
      else toast.error('生成に失敗しました。時間をおいて再度お試しください')
    },
  })

  const handleClose = () => {
    if (mutation.isPending) return
    reset()
    onClose()
  }

  const canGenerate = selected.length > 0 && (quota?.can_generate ?? true) && !mutation.isPending

  return (
    <>
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose() }}>
      {/* ダイアログは上下中央配置（top-1/2 + -translate-y-1/2）なので、下端が
          ボトムナビ（fixed bottom-0・z-60）に潜らないよう高さの上限を絞る。
          中央配置ゆえ上下に同じ余白が付くため、ナビ高さ＋セーフエリア＋余白を
          2倍して引く。vh はモバイルSafariでブラウザUIを含む大きい方の高さに
          なるため svh を使う。 */}
      <DialogContent
        className="flex flex-col"
        style={{
          height: 'min(85svh, calc(100svh - 2 * (var(--bottom-nav-h) + env(safe-area-inset-bottom) + 0.5rem)))',
        }}
      >
        <DialogHeader>
          <DialogTitle>オリジナルトロフィーを生成</DialogTitle>
        </DialogHeader>

        {quota && (
          <div className="rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
            保有 {quota.hold_count}/{quota.max_hold}
            {quota.remaining_free > 0
              ? ` ・ 無料生成 残り${quota.remaining_free}回`
              : ` ・ 本日 ${quota.generated_today}/${quota.daily_limit}回`}
            {!quota.can_generate && <span className="ml-1 text-destructive">（今は生成できません）</span>}
          </div>
        )}

        <div className="flex items-center justify-between gap-2">
          <p className="text-xs text-muted-foreground">
            材料 {selected.length}/{maxMaterials} 選択中（実績・トロフィーを組み合わせ可）
          </p>
          {selected.length > 0 && (
            <button
              type="button"
              onClick={() => setSelected([])}
              className="shrink-0 text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
            >
              クリア
            </button>
          )}
        </div>

        <div className="flex gap-1 rounded-full bg-muted p-1">
          {(['achievement', 'trophy'] as Kind[]).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setTab(k)}
              className={`flex-1 rounded-full py-1.5 text-xs font-medium transition-colors ${
                tab === k ? 'bg-background shadow-sm' : 'text-muted-foreground'
              }`}
            >
              {k === 'achievement' ? '実績から' : 'トロフィーから'}
            </button>
          ))}
        </div>

        {tab === 'achievement' && (
          <div className="space-y-2">
            {/* 検索＋絞り込みトグル */}
            <form
              onSubmit={(e) => { e.preventDefault(); setFilters((f) => ({ ...f, search: searchInput.trim() || undefined })) }}
              className="flex items-center gap-2"
            >
              <Input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="実績を検索（Enterで確定）"
                autoComplete="off"
                className="flex-1"
              />
              <button
                type="button"
                onClick={() => setFilterOpen((v) => !v)}
                className={`relative shrink-0 flex items-center justify-center rounded-full border p-2 transition-colors ${
                  activeFilterCount > 0 || filterOpen
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-border text-muted-foreground'
                }`}
              >
                <SlidersHorizontal size={16} />
                {activeFilterCount > 0 && (
                  <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[9px] font-bold text-primary-foreground ring-2 ring-background">
                    {activeFilterCount}
                  </span>
                )}
              </button>
            </form>

            {/* ソート */}
            <div className="flex gap-1.5 overflow-x-auto pb-1 [&::-webkit-scrollbar]:hidden">
              {SORT_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setFilters((f) => ({ ...f, ordering: opt.value }))}
                  className={`shrink-0 rounded-full border px-3 py-1 text-xs transition-colors ${
                    filters.ordering === opt.value
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-border bg-background text-muted-foreground'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {/* 絞り込みパネル */}
            {filterOpen && (
              <div className="space-y-3 rounded-lg border p-3">
                <div>
                  <p className="mb-1.5 text-xs font-medium text-muted-foreground">達成日</p>
                  <div className="flex flex-wrap gap-1.5">
                    {presets.map((p) => {
                      const active = (p.from || undefined) === filters.date_from && (p.to || undefined) === filters.date_to
                      return (
                        <button
                          key={p.label}
                          type="button"
                          onClick={() => applyPreset(p.from, p.to)}
                          className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${
                            active ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-background text-muted-foreground'
                          }`}
                        >
                          {p.label}
                        </button>
                      )
                    })}
                  </div>
                </div>
                <RatingFilterRow
                  label="自己評価"
                  options={SELF_RATING_OPTIONS}
                  selected={filters.rating}
                  onToggle={(v) => toggleRating('rating', v)}
                />
                <RatingFilterRow
                  label="AI評価"
                  options={AI_RATING_OPTIONS}
                  selected={filters.ai_rating}
                  onToggle={(v) => toggleRating('ai_rating', v)}
                />
              </div>
            )}
          </div>
        )}

        {/* 材料リスト */}
        <div className="flex-1 space-y-1.5 overflow-y-auto">
          {tab === 'achievement'
            ? achievements.map((a) => (
                <MaterialRow
                  key={a.id}
                  selected={isSel('achievement', a.id)}
                  title={a.title}
                  sub={`自己評価 ${a.rating}${a.ai_rating != null ? ` ・ AI評価 ${a.ai_rating}` : ''}`}
                  onClick={() => toggle('achievement', a.id)}
                  onDetail={() => setDetailAch(a)}
                />
              ))
            : trophies.map((t) => (
                <MaterialRow
                  key={t.id}
                  selected={isSel('trophy', t.id)}
                  title={t.name}
                  sub={`${t.title ? `${t.title}・` : ''}レア度 ${t.rarity}`}
                  onClick={() => toggle('trophy', t.id)}
                  onDetail={() => setDetailTrophy(t)}
                />
              ))}
          {tab === 'achievement' && achievements.length === 0 && (
            <p className="py-6 text-center text-sm text-muted-foreground">該当する実績がありません</p>
          )}
          {tab === 'trophy' && trophies.length === 0 && (
            <p className="py-6 text-center text-sm text-muted-foreground">まだトロフィーがありません</p>
          )}
        </div>

        <textarea
          value={supplement}
          onChange={(e) => setSupplement(e.target.value)}
          placeholder="補足（背景・工夫・学びなど。就活で語れる文脈を書くとレア度が上がりやすい）"
          rows={3}
          className="w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />

        <Button
          type="button"
          className="min-h-[44px] w-full"
          disabled={!canGenerate}
          onClick={() => mutation.mutate()}
        >
          {mutation.isPending ? '生成中...' : '生成する'}
        </Button>
      </DialogContent>
    </Dialog>

    {/* 材料の「詳細」ボタンで開く閲覧用ダイアログ。実績一覧と同じモーダルを
        アクション（詳細遷移・削除）なしで表示する。トロフィーは onDelete を渡さず削除ボタン非表示。 */}
    <AchievementDetailDialog achievement={detailAch} onClose={() => setDetailAch(null)} />
    <TrophyDetailDialog trophy={detailTrophy} onClose={() => setDetailTrophy(null)} />
    </>
  )
}

function MaterialRow({
  selected, title, sub, onClick, onDetail,
}: {
  selected: boolean
  title: string
  sub: string
  onClick: () => void
  onDetail?: () => void
}) {
  return (
    <div
      className={`flex w-full items-center gap-2 rounded-lg border pr-2 transition-colors ${
        selected ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'
      }`}
    >
      <button
        type="button"
        onClick={onClick}
        className="flex min-w-0 flex-1 items-center gap-3 py-2 pl-3 text-left"
      >
        <span
          className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${
            selected ? 'border-primary bg-primary text-primary-foreground' : 'border-muted-foreground/30'
          }`}
        >
          {selected && <Check size={12} />}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">{title}</p>
          <p className="truncate text-xs text-muted-foreground">{sub}</p>
        </div>
      </button>
      {onDetail && (
        <button
          type="button"
          onClick={onDetail}
          aria-label="詳細を見る"
          className="flex min-h-[44px] shrink-0 items-center gap-1 rounded-md px-2 text-xs text-muted-foreground hover:text-foreground"
        >
          <Info size={14} />
          詳細
        </button>
      )}
    </div>
  )
}
